import { AppBar, Grid, makeStyles, Tab, Tabs } from '@material-ui/core';
import i18n from 'i18n';
import { EnvironmentFields } from 'modules/environments/interfaces';
import React from 'react';
import { useHistory, useParams } from 'react-router';
import WarningOutlinedIcon from '@material-ui/icons/WarningOutlined';
import clsx from 'clsx';
import ApplicationState from 'store/interfaces';
import { connect } from 'react-redux';
import {
  RolesEnum,
  PermissionsData,
  CompanyData,
} from 'modules/authenticate/interfaces';
import {
  inAdminView,
  inOperatorView,
  inSuperAdminView,
  isClientOperator,
} from 'utils/role';
import styles from './styles';
import { requiredPerformancePermissions } from '../Performance';

const useStyles = makeStyles(styles);

interface Props {
  environment: EnvironmentFields | null;
  role?: RolesEnum;
  companySelected: CompanyData | null;
  permissions: PermissionsData | undefined;
  current:
    | 'instances'
    | 'servers'
    | 'configuration'
    | 'history'
    | 'maintenance'
    | 'audit'
    | 'performance'
    | 'sessions';
}

const NavBarEnvironments = ({
  current,
  environment,
  role,
  companySelected,
  permissions,
}: Props) => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();

  const items = [
    {
      label: i18n.t('INSTANCES_KEY'),
      slug: 'instances',
      hidden: !!permissions && !permissions.list_environment_instances,
    },
    {
      label: i18n.t('SERVERS_KEY'),
      slug: 'servers',
      hidden:
        !permissions?.list_preparation_servers &&
        !permissions?.list_support_servers &&
        !permissions?.list_models &&
        !permissions?.publish_image &&
        !permissions?.publish_image_v2,
    },
    {
      label: i18n.t('SETTINGS_KEY'),
      slug: 'configuration',
      hidden:
        !!permissions &&
        !permissions?.edit_environment &&
        !permissions?.list_applications_environment,
    },
    {
      label: i18n.t('SESSIONS_KEY'),
      slug: 'sessions',
      hidden: !permissions?.list_environment_instance_sessions,
    },
    {
      label: i18n.t('PERFORMANCE_PAGE.PERFORMANCE_KEY'),
      slug: 'performance',
      hidden:
        (!!permissions &&
          requiredPerformancePermissions.every(
            permission => !permissions[permission]
          )) ||
        isClientOperator(role),
    },
    {
      label: i18n.t('HISTORY_SOP_KEY'),
      slug: 'history',
      hidden: isClientOperator(role),
    },
    {
      label: i18n.t('MAINTENANCE_KEY'),
      slug: 'maintenance',
      icon: environment?.in_maintenance ? (
        <WarningOutlinedIcon
          color="error"
          className={classes.maintenanceIcon}
        />
      ) : (
        <></>
      ),
      hidden:
        !permissions?.get_environment_maintenance &&
        !permissions?.can_list_env_scheduled_warnings_details,
    },
  ].filter(item => !item?.hidden);

  return (
    <Grid item xs={12} className={classes.rootBar}>
      <AppBar
        position="static"
        color="default"
        className={clsx({
          [classes.barGreen]: inAdminView(role, companySelected),
          [classes.barGray]: inSuperAdminView(role, companySelected),
          [classes.barBlue]: inOperatorView(role, companySelected),
          [classes.barYellow]: companySelected,
        })}
      >
        <Tabs
          value={items.findIndex(item => item.slug === current)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {items.map(({ label, slug, icon }) => (
            <Tab
              id={`navbar-${slug}`}
              key={slug}
              label={label}
              icon={icon}
              className={clsx({
                [`${classes.tabGreen} ${classes.tab}`]: inAdminView(
                  role,
                  companySelected
                ),
                [`${classes.tabGray} ${classes.tab}`]: inSuperAdminView(
                  role,
                  companySelected
                ),
                [`${classes.tabBlue} ${classes.tab}`]: inOperatorView(
                  role,
                  companySelected
                ),
                [`${classes.tabYellow} ${classes.tab}`]: companySelected,
              })}
              onClick={() => history.push(`/environments/${id}/${slug}`)}
            />
          ))}
        </Tabs>
      </AppBar>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { data, companySelected },
}: ApplicationState) => ({
  role: data?.role,
  permissions: data?.permissions,
  companySelected,
});

export default connect(mapStateToProps, {})(NavBarEnvironments);
